import { createFileRoute } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { useUserStore } from '@/store/userStore'
import { contactService } from '@/lib/api/services/contact-service'
import { useToast } from '@/components/ui/use-toast'
import { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { guardAdminOnly } from '@/lib/auth/routeGuards'

export const Route = createFileRoute('/contact')({
  component: RouteComponent,
  beforeLoad: guardAdminOnly,
})

function RouteComponent() {
  return <ContactForm />
}

function ContactForm() {
  const { t } = useTranslation('common')
  const { toast } = useToast()
  const { user } = useUserStore()
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      address: '',
    },
    onSubmit: async ({ value }) => {
      try {
        setIsSubmitting(true)

        // Check if user is logged in
        if (!user) {
          toast({
            title: t('contact.form.errors.title', 'Error'),
            description: t('contact.form.errors.notLoggedIn', 'You must be logged in to submit this form.'),
            variant: 'destructive',
          })
          navigate({ to: '/login' })
          return
        }

        // Check if user is ADMIN or SUPER_ADMIN
        const role = user.role?.toUpperCase()
        if (role !== 'ADMIN' && role !== 'SUPERADMIN') {
          toast({
            title: t('contact.form.errors.title', 'Error'),
            description: t('contact.form.errors.unauthorized', 'Only administrators can submit upgrade requests.'),
            variant: 'destructive',
          })
          navigate({ to: '/unauthorized' })
          return
        }

        // Get etablissementId from user (include it in the request)
        const etablissementId = user.establishmentId || null

        const contactData = {
          etablissementId,
          name: value.name,
          email: value.email,
          phone: value.phone,
          address: value.address,
        }

    // console.log📧 Submitting upgrade request:', contactData)

        await contactService.submitUpgradeRequest(contactData)

        toast({
          title: t('contact.form.success.title', 'Success'),
          description: t('contact.form.success.message', 'Your upgrade request has been submitted successfully.'),
        })

        // Reset form
        form.reset()
      } catch (error: any) {
        // console.error('Error submitting contact form:', error)
        toast({
          title: t('contact.form.errors.title', 'Error'),
          description: error?.response?.data?.message || t('contact.form.errors.message', 'Failed to submit upgrade request. Please try again.'),
          variant: 'destructive',
        })
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  // Pre-fill email if user is logged in
  useEffect(() => {
    if (user?.email && !form.state.values.email) {
      form.setFieldValue('email', user.email)
    }
  }, [user?.email])

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="w-full max-w-2xl">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('contact.title', 'Contact Us')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              {t('contact.description', 'Please fill out the form below and we will get back to you as soon as possible.')}
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="space-y-6"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('contact.form.fields.name.label', 'Name')}
              </label>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => {
                    if (!value || value.trim().length === 0) {
                      return t('contact.form.fields.name.required', 'Name is required')
                    }
                    return undefined
                  },
                }}
                children={(field) => (
                  <>
                    <input
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder={t('contact.form.fields.name.placeholder', 'Enter your full name')}
                    />
                    {field.state.meta.errors && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </>
                )}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('contact.form.fields.email.label', 'Email')}
              </label>
              <form.Field
                name="email"
                validators={{
                  onChange: ({ value }) => {
                    if (!value || value.trim().length === 0) {
                      return t('contact.form.fields.email.required', 'Email is required')
                    }
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                      return t('contact.form.fields.email.invalid', 'Please enter a valid email address')
                    }
                    return undefined
                  },
                }}
                children={(field) => (
                  <>
                    <input
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      type="email"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder={t('contact.form.fields.email.placeholder', 'Enter your email address')}
                    />
                    {field.state.meta.errors && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </>
                )}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('contact.form.fields.phone.label', 'Phone')}
              </label>
              <form.Field
                name="phone"
                validators={{
                  onChange: ({ value }) => {
                    if (!value || value.trim().length === 0) {
                      return t('contact.form.fields.phone.required', 'Phone number is required')
                    }
                    // Check if only numbers
                    if (!/^\d+$/.test(value)) {
                      return t('contact.form.fields.phone.numbersOnly', 'Phone number must contain only numbers')
                    }
                    // Check length (6-10 characters)
                    if (value.length < 6 || value.length > 10) {
                      return t('contact.form.fields.phone.length', 'Phone number must be between 6 and 10 digits')
                    }
                    return undefined
                  },
                }}
                children={(field) => (
                  <>
                    <input
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        // Only allow numbers
                        const numericValue = e.target.value.replace(/\D/g, '')
                        field.handleChange(numericValue)
                      }}
                      type="tel"
                      maxLength={10}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder={t('contact.form.fields.phone.placeholder', 'Enter your phone number (6-10 digits)')}
                    />
                    {field.state.meta.errors && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </>
                )}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('contact.form.fields.address.label', 'Address')}
              </label>
              <form.Field
                name="address"
                validators={{
                  onChange: ({ value }) => {
                    if (!value || value.trim().length === 0) {
                      return t('contact.form.fields.address.required', 'Address is required')
                    }
                    return undefined
                  },
                }}
                children={(field) => (
                  <>
                    <textarea
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                      placeholder={t('contact.form.fields.address.placeholder', 'Enter your address')}
                    />
                    {field.state.meta.errors && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </>
                )}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting || form.state.isSubmitting}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting || form.state.isSubmitting
                ? t('contact.form.button.submitting', 'Submitting...')
                : t('contact.form.button.submit', 'Submit')}
            </button>
          </form>

          {/* Contact Information */}
          <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              {t('contact.info.title', 'Contact Information')}
            </h3>
            <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <p>{t('contact.info.address', 'Tunis, Tunisia')}</p>
              <p>Email: {t('contact.info.email', 'support@jeridschool.com')}</p>
              <p>Website: {t('contact.info.website', 'www.jeridschool.com')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
